import React, { useCallback, useEffect, useState } from 'react'
import {
  CAlert,
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CFormLabel,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilBolt, cilCheckCircle, cilClock, cilPeople, cilTask } from '@coreui/icons'

import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import UserService from 'src/service/UserService'
import PaidUserRequest from './PaidUserRequest'

// entry_additional_info.confirmation_type — 1 = Admin Approval, 2 = Automatically Approved.
const AUTOMATIC_APPROVAL = 2

const MEMBER_SEARCH_FIELDS = [{ key: 'full_name', label: 'Member' }]

const StatCard = ({ label, value, icon, tone }) => (
  <CCol sm={6} lg={3}>
    <CCard className="h-100">
      <CCardBody className="d-flex justify-content-between align-items-center">
        <div>
          <div className="text-medium-emphasis small">{label}</div>
          <div className="fs-4 fw-semibold">{value}</div>
        </div>
        <div
          className={`d-flex align-items-center justify-content-center rounded-circle bg-${tone}-subtle text-${tone}`}
          style={{ width: 42, height: 42 }}
        >
          <CIcon icon={icon} size="lg" />
        </div>
      </CCardBody>
    </CCard>
  </CCol>
)

const GamePassMember = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [details, setDetails] = useState([])
  const [userDetails, setUserDetails] = useState({})
  // Both the summary cards and the auto-approve notice are fed by the same response the
  // Verification Requests table already fetches, so there is one source of truth for the mode.
  const [requestMeta, setRequestMeta] = useState({})

  const isAutoApprove = requestMeta?.confirmationType === AUTOMATIC_APPROVAL

  const columns = [
    { label: 'Member', key: 'full_name', filter: false, sorter: true, _style: { width: '30%' } },
    { label: 'Email', key: 'email', filter: false, sorter: false, _style: { width: '40%' } },
    {
      label: 'Payment',
      key: 'payment_status',
      filter: false,
      sorter: false,
      _style: { width: '15%' },
    },
    { key: 'actions', label: 'Actions', filter: false, sorter: false, _style: { width: '15%' } },
  ]

  const fetchUsers = useCallback(() => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    UserService.getTeamMember(offset, itemsPerPage, activePage, params)
      .then((result) => setUsers(result.data))
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const toggleDetails = (id) => {
    setDetails(details.includes(id) ? [] : [id])
    UserService.getUserDetail(id).then((res) => {
      if (res.status === 200) setUserDetails(res.data)
    })
  }

  const markAsPaid = (userId, status) => {
    UserService.markAsPaidOrUnPaid(3, userId, status, 2).then((result) => {
      ToastComponent(result.message, 'success')
      fetchUsers()
    })
  }

  const applySearch = (value) => {
    setActivePage(1)
    setColumnFilter(value ? { [searchField]: value } : {})
  }

  const summary = requestMeta?.summary
  const totalRequests = Number(summary?.total) || 0
  const rejectedRequests = Number(summary?.rejected) || 0
  // With automatic approval nothing sits in a queue: every request that was not explicitly
  // rejected counts as approved, whatever is_paid still says on the row.
  const pendingRequests = isAutoApprove ? 0 : Number(summary?.pending) || 0
  const approvedRequests = isAutoApprove
    ? totalRequests - rejectedRequests
    : Number(summary?.approved) || 0

  const activeSearchLabel = MEMBER_SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <CRow className="g-3 mb-3">
        <StatCard label="Total Requests" value={totalRequests} icon={cilTask} tone="primary" />
        {!isAutoApprove && (
          <StatCard label="Pending Review" value={pendingRequests} icon={cilClock} tone="warning" />
        )}
        <StatCard label="Approved" value={approvedRequests} icon={cilCheckCircle} tone="success" />
        <StatCard label="Members" value={users?.total ?? 0} icon={cilPeople} tone="info" />
      </CRow>

      {isAutoApprove && (
        <CAlert color="info" className="d-flex align-items-start">
          <CIcon icon={cilBolt} className="me-2 mt-1 flex-shrink-0" />
          <div>
            <strong>This game is set to Automatically Approved.</strong> Members are approved as
            soon as they submit their verification, so there is nothing here to approve — you can
            still reject a member if you need to revoke their entry. Switch Game Entry Settings to{' '}
            <em>Admin Approval</em> if you want to review each request before it is granted.
          </div>
        </CAlert>
      )}

      <CCard className="mb-4">
        <CCardHeader className="d-flex align-items-center">
          <CIcon icon={cilTask} className="me-2" />
          <strong>Verification Requests</strong>
        </CCardHeader>
        <CCardBody>
          <PaidUserRequest onMetaChange={setRequestMeta} />
        </CCardBody>
      </CCard>

      {/* <CCard className="mb-4">
        <CCardHeader className="d-flex align-items-center">
          <CIcon icon={cilPeople} className="me-2" />
          <strong>Manage Members</strong>
        </CCardHeader>
        <CCardBody>
          <TableSearchToolbar
            fields={MEMBER_SEARCH_FIELDS}
            field={searchField}
            onFieldChange={setSearchField}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
          />
          <div className="ge-datatable ge-datatable--no-select">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              scopedColumns={{
                full_name: (item) => <td className="fw-semibold">{item.full_name}</td>,
                email: (item) => <td>{item.email}</td>,
                payment_status: (item) => {
                  const isPaid = Number(item.payment_is_paid) === 1
                  return (
                    <td>
                      <CBadge
                        color={isPaid ? 'success' : 'danger'}
                        shape="rounded-pill"
                        className="px-2 py-1"
                      >
                        {isPaid ? 'Paid' : 'Unpaid'}
                      </CBadge>
                    </td>
                  )
                },
                actions: (item) => {
                  const isPaid = Number(item.payment_is_paid) === 1
                  return (
                    <td>
                      <RowActionsMenu
                        actions={[
                          {
                            label: details.includes(item.id) ? 'Hide Details' : 'Details',
                            onClick: () => toggleDetails(item.id),
                          },
                          {
                            // payment_type 1 is an online (Stripe) payment — not ours to flip.
                            label: isPaid ? 'Mark as Unpaid' : 'Mark as Paid',
                            danger: isPaid,
                            hidden: Number(item.payment_type) === 1,
                            onClick: () => markAsPaid(item.id, isPaid ? 0 : 1),
                          },
                        ]}
                      />
                    </td>
                  )
                },
                details: (item) => (
                  <CCollapse visible={details.includes(item.id)}>
                    <CCardBody className="bg-body-tertiary">
                      <CRow className="g-2">
                        <CCol xs="auto">
                          <CFormLabel className="fw-bold mb-0">Name:</CFormLabel>
                          <span className="px-2">{userDetails?.full_name}</span>
                        </CCol>
                        <CCol xs="auto">
                          <CFormLabel className="fw-bold mb-0">Email:</CFormLabel>
                          <span className="px-2">{userDetails?.email}</span>
                        </CCol>
                        <CCol xs="auto">
                          <CFormLabel className="fw-bold mb-0">Joined:</CFormLabel>
                          <span className="px-2">
                            {userDetails?.created_at
                              ? moment(userDetails.created_at).format('D MMM YYYY')
                              : '-'}
                          </span>
                        </CCol>
                        <CCol xs="auto">
                          <CFormLabel className="fw-bold mb-0">Phone:</CFormLabel>
                          <span className="px-2">{userDetails?.phone || '-'}</span>
                        </CCol>
                        <CCol xs="auto">
                          <CFormLabel className="fw-bold mb-0">Team Name:</CFormLabel>
                          <span className="px-2">{userDetails?.my_team_name || '-'}</span>
                        </CCol>
                      </CRow>
                    </CCardBody>
                  </CCollapse>
                ),
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              noItemsLabel="No members have joined this game yet."
              pagination={{ external: true }}
              paginationProps={{
                activePage: activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard> */}
    </>
  )
}

export default GamePassMember
